import $ from 'jquery';
import _ from 'underscore';
import { TRANSITION_SPEED_MEDIUM } from '@/util/constants';
import AppVersion from '@/util/app_version';
import { onDOMReady } from '@/util/onload';

const Global = {};

Global.$document = $(document);
Global.$html = $(document.documentElement);
Global.$body = $(document.body);
Global.$window = $(window);

/**
 * @param {string} tagName
 * @returns {JQuery}
 */
Global.jqCreateElem = tagName => $(document.createElement(tagName));

/**
 * @param {...string} ids - Element ID(s)
 * @returns {JQuery}
 */
Global.jqGetElemById = (...ids) => {
  const elements = [];
  ids.forEach(id => {
    const elem = document.getElementById(id);
    if (elem) {
      elements.push(elem);
    }
  });
  return $(elements);
};

/**
 * @param {...string} cmps - Component name(s)
 * @returns {JQuery}
 */
Global.jqGetElemByCmpName = (...cmps) => {
  const ids = cmps.map(cmp => `cmp_${cmp}_id`);
  return Global.jqGetElemById(...ids);
};

const bodyData = Global.$body.data();

/**
 * Get the app version from the webview
 * @returns {AppVersion}
 */
function getCurrentAppVersion() {
  if (bodyData.appVersion) {
    return new AppVersion(bodyData.appVersion);
  }
  const matches = window.navigator.userAgent.match(/brilliant-(android|ios)-(.*?)$/);
  const appVersion = matches !== null && matches.length ? matches[2] : null;
  return new AppVersion(appVersion);
}

Global.isIos = bodyData.isIos === true || navigator.userAgent.indexOf('brilliant-ios-') >= 0;
Global.isIosWeb = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
Global.iosVersion = {};
Global.isAndroid =
  bodyData.isAndroid === true || navigator.userAgent.indexOf('brilliant-android-') >= 0;
Global.isAndroidWeb = navigator.userAgent.toLowerCase().indexOf('android') !== -1;
Global.isNative = Global.isIos || Global.isAndroid;
Global.isMobile = bodyData.isMobile === true || Global.isNative;
Global.appVersion = getCurrentAppVersion();
Global.isTouchDevice = 'ontouchstart' in document.documentElement;
Global.userIsAuthenticated = Global.$body.hasClass('active');
Global.isBrilliantPremium = Global.$body.hasClass('b2');
Global.media_host = bodyData.mediaHost;
Global.thirdPartyCookiesEnabled = bodyData.thirdPartyCookiesEnabled === true;

Global.instantTryItYourself = Global.$body.hasClass('instant-try-it-yourself');

Global.smallPadding = 8;
Global.mediumPadding = Global.smallPadding * 2;
Global.largePadding = Global.smallPadding * 3;

// detect iOS version
if (Global.isIosWeb) {
  const match = /OS (\d+_\d+(_\d+)?)/.exec(navigator.appVersion);
  if (match) {
    let version = match[1];
    if (!match[2]) {
      // add missing patch
      version += '_0';
    }
    version = version.replace(/_/g, '.');
    Global.iosVersion = new AppVersion(version);
  }
}

/**
 * Detect if the browser supports animation
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Animations/Detecting_CSS_animation_support
 * @return {Bool}
 */
Global.supportsAnimation = (function () {
  var animation = null;

  return function () {
    if (animation !== null) {
      return animation;
    }
    animation = false;
    var animationstring = 'animation',
      keyframeprefix = '',
      domPrefixes = ['Webkit', 'Moz', 'O', 'ms', 'Khtml'],
      pfx = '',
      elm = document.createElement('div');

    if (elm.style.animationName !== undefined) {
      animation = true;
    }
    if (animation === false) {
      for (var i = 0; i < domPrefixes.length; i++) {
        if (elm.style[domPrefixes[i] + 'AnimationName'] !== undefined) {
          pfx = domPrefixes[i];
          animationstring = pfx + 'Animation';
          keyframeprefix = '-' + pfx.toLowerCase() + '-';
          animation = true;
          break;
        }
      }
    }
    return animation;
  };
})();

// valid image file extenstions
window.get_valid_file_extensions = function () {
  return ['gif', 'jpg', 'jpeg', 'png'];
};

// add a dummy pushState function so that we don't always have to check if it exists
window.history.pushState = window.history.pushState || $.noop;

/**
 * Comma-formatted numbers
 * @param {int} n
 * @returns comma-formatted number - 12345 => 12,345
 */
Global.commaFormat = function (n) {
  n = '' + n;
  var decimals = '';
  if (n.indexOf('.') > -1) {
    n = n.split('.')[0];
  }
  if (n.length > 3) {
    return Global.commaFormat(n.substring(0, n.length - 3)) + ',' + n.substring(n.length - 3);
  }
  if (decimals.length) {
    return n + '.' + decimals;
  }
  return n;
};

/** @returns {string} */
Global.getRelativeUrl = function () {
  return location.pathname + location.search;
};

/**
 * Rebinds event handlers.
 * Note that it removes all handlers that were previously attached to this event. If you only want
 * to remove the specified event handler, you should add a namespace to the event name.
 * @param {JQuery} $elements
 * @param args - Any set of arguments that $.fn.on accepts (see http://api.jquery.com/on/)
 * @returns {JQuery}
 */
Global.jqOffOn = ($elements, ...args) =>
  $elements.each((i, elem) => {
    $(elem)
      .off(args[0])
      .on(...args);
  });

/**
 * Sets scrollTop for the document or an arbitrary container.
 * Any scrolling should be triggered with this utility.
 * @param {number} position
 * @param {JQuery} [$container]
 * @param {boolean} [animate]
 * @param {number} [delay] - Only used when animate == true
 * @param {number} [duration] - Only used when animate == true
 * @param {function} [callback]
 */
Global.setScrollPosition = (
  position,
  {
    $container = Global.$html.add(Global.$body),
    animate = false,
    delay = 0,
    duration = TRANSITION_SPEED_MEDIUM,
    callback = _.noop,
  } = {},
) => {
  if (animate) {
    if (delay) {
      $container.delay(delay);
    }
    $container.animate(
      {
        scrollTop: position,
      },
      {
        duration,
        complete: _.once(callback),
      },
    );
  } else {
    $container.scrollTop(position);
    callback();
  }
};

/**
 * @param {JQuery} $trigger
 */
Global.toggleComponentDebug = function ($trigger) {
  $trigger.on('click', function (e) {
    e.preventDefault();
    $('div.debug_component_info').toggle();
  });
};

// Browser Compatibility
// NOTE: if this ever becomes inadequate, we should replace it
// with something more well-rounded like Modernizr
// http://modernizr.com/
// --------------------------------------------------

let ieVersion = window.ie_version || false;

onDOMReady(() => {
  const classesToRemove = [];
  const classesToAdd = [];
  const isEdge = /Edge/.test(navigator.userAgent);

  // If this script is running, js is working
  classesToRemove.push('no-js');
  classesToAdd.push('js');

  // MS Edge?
  classesToAdd.push(isEdge ? 'msedge' : 'not-msedge');

  // IE
  if (
    ieVersion !== false ||
    /msie/i.test(navigator.userAgent) ||
    /rv:1./.test(navigator.userAgent)
  ) {
    classesToAdd.push('ie');

    // IE 10 doesn't apply ie_version correctly
    if (!ieVersion) {
      try {
        // try to parse the ie version
        ieVersion = parseInt(navigator.userAgent.match(/msie (\d{1,2})/i)[1], 10);
      } catch (e) {
        // assume 10+ otherwise (I don't really care after that)
        ieVersion = 10;
      }
    }

    if (ieVersion === -1) {
      classesToAdd.push('ie-noncompatible');
    }
    if (ieVersion >= 9) {
      classesToAdd.push('ie-modern');
    }
    if (ieVersion >= 6) {
      classesToAdd.push(`ie-${ieVersion}`);
    }
  } else {
    const ua = navigator.userAgent.toLowerCase();
    if (ua.indexOf('applewebkit') !== -1) {
      classesToAdd.push('webkit');
      if (typeof window.chrome === 'object') {
        classesToAdd.push('chrome');
      }
    } else if (ua.indexOf('gecko') !== -1) {
      classesToAdd.push('mozilla');
    } else if (ua.indexOf('presto') !== -1) {
      classesToAdd.push('opera');
      if (ua.indexOf('opera mini') !== -1) {
        classesToAdd.push('opera-mini');
      }
    } else if (ua.indexOf('series40') !== -1) {
      classesToAdd.push('s40');
    }

    // detect android version
    const matchResult = ua.match(/android\s([0-9.]*)/);
    if (matchResult !== null) {
      const androidVersion = parseFloat(matchResult[1]);
      if (androidVersion >= 4.4) {
        classesToAdd.push('android-newer');
      } else {
        classesToAdd.push('android-older');
      }
    }

    // eslint-disable-next-line no-underscore-dangle
    const _cio = window._cio || null;
    if (ua.indexOf('android') !== -1 && _cio !== null) {
      _cio.track('logged_in_with_android_browser');
    }
  }

  if (!Global.isTouchDevice) {
    classesToAdd.push('no-touch');
  }

  Global.$body.removeClass(classesToRemove.join(' ')).addClass(classesToAdd.join(' '));
});

// Detect Facebook login
// --------------------------------------------------
// Example:
//  fb_auth_status(function (logged_in, response) {
//      if(logged_in) {
//          // do user is logged in...
//          // `response` is the full response
//          // from Facebook.
//      } else {
//          // user is logged out...
//      }
//  });
//
// NOTE: Your code should be ok with Facebook not
// responding.

window._fb_status = window._fb_status || [];
window.fb_auth_status = function (callback) {
  _fb_status.push(callback || function () {});
};

// fb_auth_status initialization moved to includes/facebook-sdk.html

// testing Sentry
if (window.location.search.indexOf('breakme') >= 0) {
  breakMe();
}

module.exports = Global;
